import React, { useRef, useEffect } from "react";

const StarfieldBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext("2d")!;
        let animationFrameId: number;

        interface Star {
            x: number;
            y: number;
            z: number;
        }

        const stars: Star[] = [];
        const numStars = 1000;

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);
        };

        const initStars = () => {
            stars.length = 0;
            const { width, height } = canvas.getBoundingClientRect();

            for (let i = 0; i < numStars; i++) {
                const angle = Math.random() * Math.PI * 2;
                const radiusFactor = Math.pow(Math.random(), 0.5);
                const radius = (Math.min(width, height) / 2) * radiusFactor * 7;

                stars.push({
                    x: Math.cos(angle) * radius,
                    y: Math.sin(angle) * radius,
                    z: Math.random() * width,
                });
            }
        };

        resize();
        initStars();
        window.addEventListener("resize", () => {
            resize();
            initStars();
        });

        const draw = () => {
            const { width, height } = canvas.getBoundingClientRect();
            ctx.fillStyle = "black";
            ctx.fillRect(0, 0, width, height);

            for (let i = 0; i < numStars; i++) {
                const star = stars[i];
                star.z -= 0.4;
                if (star.z <= 0) star.z = width;

                const k = 128.0 / star.z;
                const px = star.x * k + width / 2;
                const py = star.y * k + height / 2;

                // Only draw if inside bounds
                if (px >= 0 && px <= width && py >= 0 && py <= height) {
                    const size = (1 - star.z / width) * 1.8;
                    const brightness = Math.min(200, (1 - star.z / width) * 200);
                    const grey = Math.floor(brightness * 0.75 + 60);
                    ctx.fillStyle = `rgb(${grey}, ${grey}, ${grey})`;
                    ctx.beginPath();
                    ctx.arc(px, py, size, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            animationFrameId = requestAnimationFrame(draw);
        };

        draw();

        return () => cancelAnimationFrame(animationFrameId);
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 0,
            }}
        />
    );
};

export default StarfieldBackground;
